import { ReactNode } from 'react'
import { CellProps } from 'react-table'
import { formatDateToPtBr } from '../../../../../_metronic/helpers'
import {
  PlainHeader,
  sortableColumn,
  StackedCell,
  TableColumn,
  UseQueryRequestHook,
} from '../../../../components'
import { EscalaList } from '../core/_models'
import { formatFaixaHorario } from '../core/escalaHelpers'
import { TipoEscalaBadge } from './TipoEscalaBadge'

type Config = {
  useQueryRequest: UseQueryRequestHook
  /** Célula de ações da aba (minha escala ou escala da instituição). */
  renderActions: (escala: EscalaList) => ReactNode
}

/**
 * Colunas das listagens de escala (minha escala e escala da instituição), que só diferem nas
 * ações. `dataInicio`/`dataTermino` são `LocalDate` puras: formatamos com `formatDateToPtBr`
 * (split de string) e não com `DataColumn`, que faz `new Date(str)` e exibiria o dia anterior no
 * fuso de São Paulo.
 */
export const escalaColumns = ({
  useQueryRequest,
  renderActions,
}: Config): ReadonlyArray<TableColumn<EscalaList>> => [
  sortableColumn<EscalaList>({
    id: 'tipo',
    cellWidth: '200px',
    useQueryRequest,
    fields: [{ field: 'tipo', label: 'Tipo' }],
    Cell: ({ row }: CellProps<EscalaList>) => <TipoEscalaBadge tipo={row.original.tipo} />,
  }),
  // Período: início como principal, término como apoio; os dois seguem ordenáveis.
  sortableColumn<EscalaList>({
    id: 'dataInicio',
    useQueryRequest,
    fields: [
      { field: 'dataInicio', label: 'Início' },
      { field: 'dataTermino', label: 'Término' },
    ],
    Cell: ({ row }: CellProps<EscalaList>) => {
      const { dataInicio, dataInicioFormatada, dataTermino, dataTerminoFormatada } = row.original
      const termino = dataTerminoFormatada || (dataTermino && formatDateToPtBr(dataTermino))
      return (
        <StackedCell
          tabular
          primary={dataInicioFormatada || formatDateToPtBr(dataInicio)}
          secondary={termino ? `até ${termino}` : undefined}
        />
      )
    },
  }),
  {
    Header: () => <PlainHeader>Horário</PlainHeader>,
    id: 'horario',
    cellWidth: '160px',
    Cell: ({ row }: CellProps<EscalaList>) => (
      <StackedCell
        tabular
        primary={formatFaixaHorario(row.original.horarioInicio, row.original.horarioTermino)}
      />
    ),
  },
  {
    Header: () => <PlainHeader>Ações</PlainHeader>,
    id: 'actions',
    cellWidth: '132px',
    align: 'end',
    Cell: ({ row }: CellProps<EscalaList>) => <>{renderActions(row.original)}</>,
  },
]
